<script setup>
import { inject, ref } from 'vue';
import { ArrowLongRightIcon } from '@heroicons/vue/24/outline';
import CooperationCard from './CooperationCard.vue';
import LogoMarquee from './LogoMarquee.vue';
import CooperationModal from './CooperationModal.vue';

const programPackage = inject('programPackage');

const displayedCooperation = ref(null);

const emit = defineEmits(['showMoreInfo']);

const cooperationsLogosForMarquee = [
    ...programPackage?.cooperations?.goldSponsoring || [],
    ...programPackage?.cooperations?.mainSponsoring || [],
    ...programPackage?.cooperations?.platinumSponsoring || [],
    ...programPackage?.cooperations?.silverSponsoring || [],
].map(sponsor => ({ name: sponsor?.profile?.company_name, logo: sponsor?.profile?.glide_logo_image_url_webp }))
    .filter(sponsor => sponsor.logo);

const splitToNChunks = (array, n) => {
    const result = [];

    for (let i = n; i > 0; i--) {
        result.push(array.splice(0, Math.ceil(array.length / i)));
    }

    return result;
};
</script>

<template>
    <section class="bg-deep-teal-50 mt-12 lg:mt-8">
        <div class="container px-4 py-8 lg:py-12 mx-auto">
            <div class="space-y-4">
                <div class="flex justify-between items-center">
                    <span
                        v-if="programPackage.overview_tab_headline"
                        class="font-semibold text-2xl text-deep-teal-700"
                    >
                        {{ programPackage.overview_tab_headline }}
                    </span>

                    <button
                        class="hidden lg:inline-flex items-center font-semibold text-base text-deep-teal-700 hover:text-deep-teal-900 hover:underline focus:underline focus:outline-none focus:text-deep-teal-900"
                        @click="emit('showMoreInfo')"
                    >
                        {{ $t('eventDetails.actions.moreInfo') }}

                        <ArrowLongRightIcon
                            class="w-5 h-5 ml-1.5"
                            aria-hidden="true"
                        />
                    </button>
                </div>

                <div
                    v-if="programPackage.overview_tab_body"
                    class="lg:w-3/4 text-gray-700"
                >
                    {{ programPackage.overview_tab_body }}
                </div>

                <button
                    class="inline-flex lg:hidden items-center font-semibold text-base text-deep-teal-700 hover:text-deep-teal-900 hover:underline focus:underline focus:outline-none focus:text-deep-teal-900"
                    @click="emit('showMoreInfo')"
                >
                    {{ $t('eventDetails.actions.moreInfo') }}

                    <ArrowLongRightIcon
                        class="w-5 h-5 ml-1.5"
                        aria-hidden="true"
                    />
                </button>
            </div>

            <div
                v-if="programPackage?.cooperations?.platinumSponsoring"
                class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-8"
            >
                <CooperationCard
                    v-for="(item, index) in programPackage?.cooperations?.platinumSponsoring"
                    :key="index"
                    :cooperation="item"
                    @click="displayedCooperation = item"
                />
            </div>

            <div
                v-if="[...programPackage?.cooperations?.goldSponsoring || [], ...programPackage?.cooperations?.silverSponsoring || []].length > 0"
                class="-mx-4 text-gray-700 mt-7 lg:gap-y-4 lg:flex lg:flex-wrap lg:divide-x columns-1 sm:columns-2"
            >
                <div
                    v-for="(group, index) in splitToNChunks([
                        ...programPackage?.cooperations?.goldSponsoring || [],
                        ...programPackage?.cooperations?.silverSponsoring || []
                    ], 4)"
                    :key="index"
                    class="w-full px-4 space-y-6 lg:w-1/4"
                >
                    <ul>
                        <li
                            v-for="(item, key) in group"
                            :key
                            class="list-disc list-outside pl-1 ml-6"
                        >
                            {{ item?.profile?.company_name }}
                        </li>
                    </ul>
                </div>
            </div>

            <div
                v-if="cooperationsLogosForMarquee.length > 0"
                class="mt-7 md:mt-10 pt-8 md:pt-12 border-t"
            >
                <LogoMarquee>
                    <div
                        v-for="(item, key) in [...cooperationsLogosForMarquee, ...cooperationsLogosForMarquee]"
                        :key
                        class="h-12 md:h-16 w-32 md:w-44 grid place-items-center"
                    >
                        <img
                            v-if="item.logo"
                            :alt="`${$t('eventDetails.cooperation.logo.alt')} ${item.name}`"
                            class="object-contain h-[inherit]"
                            :src="item.logo"
                        >
                    </div>
                </LogoMarquee>
            </div>
        </div>
    </section>

    <CooperationModal
        v-if="displayedCooperation"
        :cooperation="displayedCooperation"
        :show="Boolean(displayedCooperation)"
        @closed="displayedCooperation = null"
    />
</template>
